// Sugalarm — Cycle 424: the low-rung latch, CLIENT HALF.
//
// A member climbing out of an urgent low has to pass back through the low
// band to get anywhere. Once an urgent low has fired, the LOW rung is
// latched off until the member fully recovers to in range — whether or not
// anyone declared they are treating. The server owns the rule
// (`alerts_service.low_rung_latched`): it creates no low row and pages no
// low rail while the latch holds. This module mirrors that predicate over
// the row the server sends, so the surfaces can (1) keep the URGENT row's
// cue rather than start a low cue, and (2) SAY WHY it is quiet.
//
// ⚠ DERIVED, NEVER STORED — on this side too. There is no flag to remember
// or clear: the latch is a function of the bound row's own columns
// (`fired_at` / `recovered_at` / `silenced_at` / `ascent_broken_at`) and
// the live reading's zone. When the server recovers the row the predicate
// is false by construction; a client cannot be left latched.
//
// ⚠ IT NEVER MAKES ANYTHING QUIETER THAN THE SERVER ALREADY HAS. The
// urgent row's audio, its controls (Snooze, "I'm treating this", never
// Stop) and every custody surface are untouched; the only thing this
// changes is that a low-band reading under an open urgent row keeps the
// URGENT cue instead of switching to the low loop, and a line explains it.

import type { Alert, AlertLevel } from "./types";

/** THE ONE DEFINITION on the client. True when `bound` is an open,
 *  unrecovered, un-silenced `urgent_low` row whose ascent has not been
 *  broken, AND the live reading currently classifies as plain `low` — i.e.
 *  the member is climbing through the low band on the way up.
 *
 *  Any other live zone is not the latch's business: in range means the
 *  server's recovery dwell is running (or done), `urgent_low` means the
 *  urgent rung is sounding on its own terms, high-side is another family. */
export function lowRungLatched(
  bound: Alert | null | undefined,
  liveLevel: AlertLevel | null,
): boolean {
  if (bound == null) return false;
  if (bound.level !== "urgent_low") return false;
  if (bound.fired_at == null) return false;
  if (bound.recovered_at != null) return false;
  if (bound.silenced_at != null) return false;
  if (bound.ascent_broken_at != null) return false;
  return liveLevel === "low";
}

/** The "say why it is quiet" line (cycle 392's rule: a rail that goes
 *  silent without saying why is worse than one that stays loud). Brand
 *  copy: plain sentences, no em dashes, no efficacy claim. */
export function ascentHoldLine(memberName: string | null | undefined): string {
  const name = memberName?.trim();
  return name
    ? `${name} is recovering from an urgent low. Low alerts are held until back in range.`
    : "Recovering from an urgent low. Low alerts are held until back in range.";
}
